import { useEffect, useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";

/**
 * The audience map (design: components/marketing/AudienceMap.tsx).
 *
 * The design draws a stylised map of Pune with its roads, rivers and sample
 * clinics. That geography is sample content, so this board draws only what is
 * real: the targeted cities (Meta's coordinates, item 40) and dropped pins as
 * circles of their radius, and the clinics (/api/facilities latitude and
 * longitude) as squares — projected around their centre and fitted to the
 * frame, with a scale bar that matches. No roads, no rivers: nothing invented.
 *
 * With dropPin on, a click becomes a latitude and longitude (onDrop).
 * A place without coordinates is still targeted; it is listed beside the map
 * but not drawn.
 */
const W = 600, H = 400;
const KM_PER_DEG_LAT = 110.57;
const kmPerDegLng = (lat) => 111.32 * Math.cos((lat * Math.PI) / 180);
const NICE_KM = [1, 2, 5, 10, 20, 50, 100, 200, 500];
// The map is about this wide beside the place list on desktop; narrower (phone), its words are scaled up to the same size.
const TEXT_AT_PX = 400;

export function AudienceMap({ locations = [], clinics = [], dropPin = false, onDrop }) {
  const [zoom, setZoom] = useState(1);
  const svg = useRef(null);
  const [text, setText] = useState(1); // label scale, 1 from TEXT_AT_PX up
  useEffect(() => {
    const el = svg.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => { const w = entry.contentRect.width; setText(w ? Math.max(1, TEXT_AT_PX / w) : 1); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const placed = locations.filter((l) => Number.isFinite(l.latitude) && Number.isFinite(l.longitude));
  const sites = clinics.filter((c) => Number.isFinite(c.latitude) && Number.isFinite(c.longitude));
  const points = [...placed, ...sites];

  // Centre on everything drawn; fit the farthest edge (radius included) into the frame.
  const centre = points.length
    ? { lat: points.reduce((s, p) => s + p.latitude, 0) / points.length, lng: points.reduce((s, p) => s + p.longitude, 0) / points.length }
    : null;
  const toKm = (p) => ({ x: (p.longitude - centre.lng) * kmPerDegLng(centre.lat), y: (p.latitude - centre.lat) * KM_PER_DEG_LAT });
  let halfW = 5, halfH = 5;
  if (centre) {
    placed.forEach((l) => { const k = toKm(l); halfW = Math.max(halfW, Math.abs(k.x) + l.km); halfH = Math.max(halfH, Math.abs(k.y) + l.km); });
    sites.forEach((c) => { const k = toKm(c); halfW = Math.max(halfW, Math.abs(k.x) + 2); halfH = Math.max(halfH, Math.abs(k.y) + 2); });
  }
  const pxPerKm = Math.min((W / 2) / (halfW * 1.1), (H / 2) / (halfH * 1.1));
  const project = (p) => { const k = toKm(p); return { x: W / 2 + k.x * pxPerKm, y: H / 2 - k.y * pxPerKm }; };

  const w = W / zoom, h = H / zoom;
  const vb = { x: W / 2 - w / 2, y: H / 2 - h / 2, w, h };
  const visibleKm = w / pxPerKm;
  const scaleKm = NICE_KM.filter((k) => k <= visibleKm / 4).pop() || 1;
  const scalePx = scaleKm * pxPerKm;

  const click = (event) => {
    if (!dropPin || !onDrop || !svg.current) return;
    const pt = svg.current.createSVGPoint();
    pt.x = event.clientX; pt.y = event.clientY;
    const local = pt.matrixTransform(svg.current.getScreenCTM().inverse());
    // With nothing on the map yet there is no centre to measure from.
    if (!centre) return;
    const xKm = (local.x - W / 2) / pxPerKm;
    const yKm = (H / 2 - local.y) / pxPerKm;
    onDrop(centre.lat + yKm / KM_PER_DEG_LAT, centre.lng + xKm / kmPerDegLng(centre.lat));
  };

  return (
    <div className="@container relative overflow-hidden rounded-md bg-mist ring-1 ring-line">
      <svg ref={svg} viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} onClick={click} className={`block aspect-[3/2] w-full select-none ${dropPin && centre ? "cursor-crosshair" : ""}`} role="img" aria-label="Map of the areas this ad targets">
        <rect x={-W} y={-H} width={W * 3} height={H * 3} className="fill-mist" />
        {centre && placed.map((l) => {
          const c = project(l);
          return <g key={l.id}>
            <circle cx={c.x} cy={c.y} r={l.km * pxPerKm} className={l.included ? "fill-sky/25 stroke-sky-deep" : "fill-danger-soft/60 stroke-danger"} strokeWidth={1.5 / zoom} strokeDasharray={l.pin ? `${4 / zoom} ${3 / zoom}` : undefined} />
            <circle cx={c.x} cy={c.y} r={3 / zoom} className={l.included ? "fill-sky-deep" : "fill-danger"} />
            <text x={c.x} y={c.y - (7 * text) / zoom} textAnchor="middle" className="fill-ink font-semibold" style={{ fontSize: (10 * text) / zoom }}>{l.name}</text>
          </g>;
        })}
        {centre && sites.map((s) => {
          const c = project(s);
          return <g key={`clinic-${s.id}`}><rect x={c.x - 4 / zoom} y={c.y - 4 / zoom} width={8 / zoom} height={8 / zoom} rx={1.5 / zoom} className="fill-sun stroke-ink" strokeWidth={1 / zoom} /><title>{s.name}</title></g>;
        })}
        <g transform={`translate(${vb.x + vb.w - scalePx - 16 / zoom} ${vb.y + vb.h - 16 / zoom})`}>
          <rect x={-6 / zoom} y={(-14 * text) / zoom} width={scalePx + 12 / zoom} height={(14 * text + 8) / zoom} rx={4 / zoom} className="fill-paper/90" />
          <line x1={0} y1={0} x2={scalePx} y2={0} className="stroke-ink" strokeWidth={2 / zoom} />
          <text x={scalePx / 2} y={-4 / zoom} textAnchor="middle" className="fill-ink font-semibold" style={{ fontSize: (9 * text) / zoom }}>{scaleKm} km</text>
        </g>
      </svg>

      <div className="absolute right-3 top-3 flex flex-col overflow-hidden rounded-sm bg-paper shadow-soft ring-1 ring-line">
        <button type="button" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(4, z + 0.5))} className="flex h-8 w-8 items-center justify-center hover:bg-mist"><Plus size={15} strokeWidth={1.75} /></button>
        <button type="button" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(1, z - 0.5))} className="flex h-8 w-8 items-center justify-center border-t border-line hover:bg-mist"><Minus size={15} strokeWidth={1.75} /></button>
      </div>
      {/* On a narrow map the key sits top left, clear of the scale bar. */}
      <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-3 rounded-sm bg-paper/90 px-2.5 py-1.5 text-caption text-muted shadow-soft @sm:bottom-3 @sm:top-auto">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-xs bg-sun ring-1 ring-ink" />Your clinics</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-sky/40 ring-1 ring-sky-deep" />Targeted</span>
      </div>
      {!centre && <p className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-caption text-muted">Add a city to see it here.</p>}
    </div>
  );
}
